import {
    externalLinksRegex,
    IL_SENTINEL_END,
    IL_SENTINEL_START,
    internalLinksRegex,
    localFilePathRegex,
    PROT_SENTINEL_END,
    PROT_SENTINEL_START,
    RAW_CONTENT_TAGS,
    SENTINEL_END,
    SENTINEL_START,
    TRAILING_PUNCT,
    UNCFilePathRegex,
    URL_ATTR_TAGS,
} from "./constants"

type Pass = {
    regex: RegExp
    toHtml: (match: string) => string
}

type Token = {
    raw: string
    index: number
}

type InternalLinkNode = {
    node: Text
    tokens: Token[]
}

type ProtectedEle = {
    sentinel: string
    html: string
}

const isInsideAnchor = (node: Node): boolean => {
    let parent = node.parentNode
    while (parent) {
        if ((parent as Element).nodeName === "A") {
            return true
        }
        parent = parent.parentNode
    }
    return false
}

//collecting the text contains internal link
const collectInternalTokens = (text: string): Token[] => {
    // Fresh regex instance so lastIndex doesn't leak between calls
    const regEx = new RegExp(internalLinksRegex.source, "gu")
    const tokens: Token[] = []
    let regExpExecArray: RegExpExecArray | null
    while ((regExpExecArray = regEx.exec(text)) !== null) {
        tokens.push({ raw: regExpExecArray[0], index: regExpExecArray.index })
    }
    return tokens
}

const runPasses = (text: string, passes: Pass[]): string => {
    const store: string[] = []
    let current = text

    for (const pass of passes) {
        // Fresh regex instance per pass to avoid stale lastIndex
        const re = new RegExp(pass.regex.source, pass.regex.flags)
        current = current.replace(re, (match) => {
            const id = store.length
            store.push(pass.toHtml(match))
            return `${SENTINEL_START}${id}${SENTINEL_END}`
        })
    }

    // Restore placeholders
    return current.replace(
        new RegExp(`${SENTINEL_START}(\\d+)${SENTINEL_END}`, "g"),
        (_, id: string) => store[Number(id)],
    )
}

const externalLinkToHtml = (raw: string): string => {
    const finalLink = raw.match(TRAILING_PUNCT)
    const trailing = finalLink ? finalLink[0] : ""
    const link = trailing ? raw.slice(0, -trailing.length) : raw

    const prefix = link.startsWith("www.") ? "http://" : ""
    const href = `${prefix}${link}`
    return (
        `<a data-ft-link-type="external" data-ft-link="${link}" href="${href}" target="_blank" rel="noopener noreferrer">${link}</a>` +
        trailing
    )
}

const localFileToHtml = (link: string): string => {
    const href = `file:///${link.replace("\\", "/")}`
    return `<a data-ft-link-type="file" data-ft-link="${href}" href="${href}" target="_blank" rel="noopener noreferrer">${link}</a>`
}

const uncFileToHtml = (link: string): string => {
    const href = `file:///${link
        .replace("\\\\?\\UNC\\", "\\\\")
        .replace("\\", "/")}`
    return `<a data-ft-link-type="file" data-ft-link="${href}" href="${href}" target="_blank" rel="noopener noreferrer">${link}</a>`
}

const passes: Pass[] = [
    { regex: externalLinksRegex, toHtml: externalLinkToHtml },
    { regex: localFilePathRegex, toHtml: localFileToHtml },
    { regex: UNCFilePathRegex, toHtml: uncFileToHtml },
]

export const urlify = async (text: string): Promise<string> => {
    return runPasses(text, passes)
}

const escapeAttrValue = (s: string): string =>
    s
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")

// Each IL sentinel becomes <span data-ilink-mount data-ilink-url>
const convertInternalTokenToSpan = (
    input: string,
    ilStore: { url: string }[],
): string => {
    let mountCounter = 0
    return input.replace(
        new RegExp(`${IL_SENTINEL_START}(\\d+)${IL_SENTINEL_END}`, "g"),
        (_, idStr: string) => {
            const { url } = ilStore[Number(idStr)]
            const mountId = `il-${mountCounter++}`
            const safeUrl = escapeAttrValue(url)
            return `<span data-ilink-mount="${mountId}" data-ilink-url="${safeUrl}"></span>`
        },
    )
}
// build final HTML with placeholder spans for internal links
export const buildHtmlWithPlaceholders = async (
    content: string,
): Promise<string> => {
    //for special character issue in link
    const normalizedContent = content
        .replace(/&amp;;/g, "&amp;")
        .replace(/&;/g, "&")

    // Necessary because of https://github.com/remarkablemark/html-dom-parser/issues/420
    const replacedContent = normalizedContent.replace(/\n/g, "<br />")
    const doc = new DOMParser().parseFromString(replacedContent, "text/html")

    // Move <script> tags into a dedicated <div id="script">
    const scripts = Array.from(doc.querySelectorAll("script"))
    if (scripts.length > 0) {
        scripts.forEach((scriptTag) => scriptTag.remove())

        const scriptContainer = doc.createElement("div")
        scriptContainer.id = "script"
        scripts.forEach((scriptTag) => scriptContainer.appendChild(scriptTag))
        doc.body.appendChild(scriptContainer)
    }

    // Protect raw-content and URL-attribute elements
    const protectedElements: ProtectedEle[] = []

    const protectElement = (el: Element): void => {
        const id = protectedElements.length
        const sentinel = `${PROT_SENTINEL_START}${id}${PROT_SENTINEL_END}`
        protectedElements.push({ sentinel, html: el.outerHTML })
        // Replace with a Text node carrying the sentinel. The sentinel
        // contains no URL-like text, so urlify ignores it entirely.
        el.replaceWith(doc.createTextNode(sentinel))
    }

    // Protect by tag name — case-insensitive via querySelectorAll
    for (const tag of RAW_CONTENT_TAGS) {
        doc.querySelectorAll(tag).forEach(protectElement)
    }
    for (const tag of URL_ATTR_TAGS) {
        doc.querySelectorAll(tag).forEach(protectElement)
    }

    // Walk text nodes and replace internal tokens with
    const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
    const work: InternalLinkNode[] = []

    while (walker.nextNode()) {
        const node = walker.currentNode as Text
        if (isInsideAnchor(node)) {
            continue
        }

        const tokens = collectInternalTokens(node.textContent || "")
        if (tokens.length > 0) {
            work.push({ node, tokens })
        }
    }

    // ---------- swap internal tokens for IL sentinels ----------
    const ilStore: { url: string }[] = []

    for (const { node, tokens } of work) {
        let text = node.textContent || ""
        // Right-to-left so earlier indices stay valid
        const sorted = [...tokens].sort((a, b) => b.index - a.index)
        for (const token of sorted) {
            const id = ilStore.length
            ilStore.push({ url: token.raw })
            const sentinel = `${IL_SENTINEL_START}${id}${IL_SENTINEL_END}`
            text =
                text.slice(0, token.index) +
                sentinel +
                text.slice(token.index + token.raw.length)
        }
        node.textContent = text
    }

    // const withILSentinels = doc.body.innerHTML
    const withILSentinels = doc.body.innerHTML.replace(/&amp;/g, "&")
    let urlified = await urlify(withILSentinels)

    /* --------------------------------------------------------------
     * Restore protected elements
     * This puts <iframe src="...">, <img src="...">, <style>...</style>, etc.
     * back exactly as they were before urlify ran.
     * -------------------------------------------------------------- */
    for (const { sentinel, html } of protectedElements) {
        urlified = urlified.replace(sentinel, html)
    }

    return convertInternalTokenToSpan(urlified, ilStore)
}

export const buildTextWithPlaceholders = async (
    content: string,
): Promise<string> => {
    const normalizedContent = content
        .replace(/&amp;;/g, "&amp;")
        .replace(/&;/g, "&")
    let text = normalizedContent.replace(/\n/g, "<br />")

    // ---------- swap internal tokens for IL sentinels ----------
    const ilStore: { url: string }[] = []
    const re = new RegExp(internalLinksRegex.source, "gu")

    text = text.replace(re, (match) => {
        const id = ilStore.length
        ilStore.push({ url: match })
        return `${IL_SENTINEL_START}${id}${IL_SENTINEL_END}`
    })

    // ---------- run urlify for external/local/UNC ----------
    const urlified = await urlify(text.replace(/&amp;/g, "&"))

    return convertInternalTokenToSpan(urlified, ilStore)
}
